
/**
 * Transaction-safe calculation utilities for seller credit and closing costs
 * Implements strict type checking and failsafe guards against invalid values
 */

/**
 * Type guard to ensure a value is a valid positive number
 */
export const isValidPositiveNumber = (value: any): value is number => {
  return typeof value === 'number' && 
         !isNaN(value) && 
         isFinite(value) && 
         value >= 0;
};

/**
 * Type guard to ensure a value is a valid number (can be zero or positive)
 */
export const isValidNumber = (value: any): value is number => {
  return typeof value === 'number' && 
         !isNaN(value) && 
         isFinite(value);
};

/**
 * Safely converts any value to a valid number with fallback
 */
export const safeNumberConversion = (value: any, fallback: number = 0): number => {
  if (isValidNumber(value)) {
    return value;
  }
  return fallback;
};

/**
 * Calculates adjusted closing costs after seller credit deduction
 * Implements strict validation and ensures credit never exceeds closing costs
 */
export const calculateAdjustedClosingCosts = (
  totalClosingCosts: number,
  sellerCreditAmount: number
): number => {
  // Validate input parameters
  const safeTotalClosingCosts = safeNumberConversion(totalClosingCosts, 0);
  const safeSellerCreditAmount = safeNumberConversion(sellerCreditAmount, 0);
  
  // Ensure seller credit doesn't exceed closing costs
  const effectiveCredit = Math.min(safeSellerCreditAmount, safeTotalClosingCosts);
  
  // Calculate adjusted costs with failsafe
  const adjustedClosingCosts = Math.max(safeTotalClosingCosts - effectiveCredit, 0);
  
  // Final validation
  if (!isValidPositiveNumber(adjustedClosingCosts)) {
    console.warn('Invalid adjusted closing costs calculated, returning fallback');
    return safeTotalClosingCosts;
  }
  
  return adjustedClosingCosts;
};

/**
 * Calculates total cash due at closing with validation
 */
export const calculateTotalCashDue = (
  downPayment: number,
  adjustedClosingCosts: number
): number => {
  // Validate input parameters
  const safeDownPayment = safeNumberConversion(downPayment, 0);
  const safeAdjustedClosingCosts = safeNumberConversion(adjustedClosingCosts, 0);
  
  // Calculate total with failsafe
  const totalCashDue = safeDownPayment + safeAdjustedClosingCosts;
  
  // Final validation
  if (!isValidPositiveNumber(totalCashDue)) {
    console.warn('Invalid total cash due calculated, returning fallback');
    return safeDownPayment + safeAdjustedClosingCosts;
  }
  
  return totalCashDue;
};

/**
 * Calculates the effective seller credit amount (cannot exceed closing costs)
 */
export const calculateEffectiveSellerCredit = (
  requestedCreditAmount: number,
  totalClosingCosts: number
): number => {
  const safeRequestedCredit = safeNumberConversion(requestedCreditAmount, 0);
  const safeTotalClosingCosts = safeNumberConversion(totalClosingCosts, 0);
  
  return Math.min(safeRequestedCredit, safeTotalClosingCosts);
};

/**
 * Validates all intermediate calculation values for transaction safety
 */
export const validateCalculationInputs = (inputs: Record<string, any>): boolean => {
  return Object.values(inputs).every(value => isValidNumber(value));
};
